import React from "react";
import type { QueueItem } from "../api";
import { api } from "../api";

type Props = {
  items: QueueItem[];
  refresh: () => Promise<void>;
};

const COLS: { key: QueueItem["status"]; label: string }[] = [
  { key: "pending", label: "Pending" },
  { key: "rendering", label: "Rendering" },
  { key: "done", label: "Done" },
  { key: "failed", label: "Failed" },
];

type SortKey = "new" | "old" | "name";

const SORTS: { key: SortKey; label: string }[] = [
  { key: "new", label: "Neueste zuerst" },
  { key: "old", label: "Älteste zuerst" },
  { key: "name", label: "Name (A–Z)" },
];

function fmtDate(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const time = d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  if (sameDay) return `Heute · ${time}`;
  return `${d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })} · ${time}`;
}

function itemLabel(item: QueueItem): string {
  return (item.title?.trim() || item.template || item.id).toString();
}

const Card: React.FC<{
  item: QueueItem;
  refresh: () => Promise<void>;
  selected: boolean;
  onToggle: (id: string) => void;
}> = ({ item, refresh, selected, onToggle }) => {
  const progress = item.runtime?.progress ?? 0;
  const cur = item.runtime?.currentTarget;
  const status = item.runtime?.status ?? item.status;
  const outputs = item.runtime?.outputs?.length ? item.runtime.outputs : item.outputs;
  const date = fmtDate(item.updatedAt || item.createdAt);

  return (
    <div className={`queue-card${selected ? " selected" : ""}`}>
      <div className="top">
        <input
          type="checkbox"
          className="queue-card-check"
          checked={selected}
          onChange={() => onToggle(item.id)}
          title="Auswählen (für Sammel-Aktionen)"
        />
        <div className="head-text">
          <div className="tpl" title={itemLabel(item)}>
            {itemLabel(item)}
          </div>
          <div className="id">
            {item.id}
            {date ? <span className="date">· {date}</span> : null}
          </div>
        </div>
        <div className="actions">
          {(status === "pending" || status === "failed") && (
            <button
              title="Rendern"
              onClick={async () => {
                await api.startRender(item.id);
                await refresh();
              }}
            >
              ▶
            </button>
          )}
          <button
            title="Entfernen"
            onClick={async () => {
              if (!confirm(`Item ${item.id} entfernen?`)) return;
              await api.removeFromQueue(item.id);
              await refresh();
            }}
          >
            🗑
          </button>
        </div>
      </div>
      <div className="meta">
        <span className="pill">{item.languages.join(" · ")}</span>
        {item.formats.map((f) => (
          <span key={f} className="pill">
            {f}
          </span>
        ))}
        {item.ideaId ? <span className="pill">{item.ideaId}</span> : null}
        {status === "done" && outputs.length > 0
          ? outputs.map((o, i) => (
              <a key={i} className="pill out" href={o.url} target="_blank" rel="noreferrer">
                ⬇ {o.format}
              </a>
            ))
          : null}
      </div>
      {status === "queued" ? (
        <div style={{ fontSize: 11, color: "var(--fg-muted)" }}>⏳ Wartet auf freien Render-Slot…</div>
      ) : null}
      {status === "rendering" ? (
        <>
          <div style={{ fontSize: 11, color: "var(--fg-muted)" }}>
            {cur ? `${cur.language} · ${cur.format}` : "starte…"} · {(progress * 100).toFixed(0)}%
          </div>
          <div className="progress-bar">
            <div style={{ width: `${progress * 100}%` }} />
          </div>
        </>
      ) : null}
      {item.runtime?.error ? <div className="err">{item.runtime.error}</div> : null}
      {item.error ? <div className="err">{item.error}</div> : null}
    </div>
  );
};

export const Queue: React.FC<Props> = ({ items, refresh }) => {
  const [sort, setSort] = React.useState<SortKey>("new");
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [busy, setBusy] = React.useState(false);
  const [concurrency, setConcurrency] = React.useState<number>(2);
  const [maxConcurrency, setMaxConcurrency] = React.useState<number>(8);

  React.useEffect(() => {
    let alive = true;
    api
      .getSettings()
      .then((s) => {
        if (!alive) return;
        setConcurrency(s.renderConcurrency);
        setMaxConcurrency(s.maxRenderConcurrency);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const onConcurrencyChange = async (next: number) => {
    setConcurrency(next); // optimistisch
    try {
      const s = await api.setRenderConcurrency(next);
      setConcurrency(s.renderConcurrency);
      setMaxConcurrency(s.maxRenderConcurrency);
    } catch {
      api.getSettings().then((s) => setConcurrency(s.renderConcurrency)).catch(() => {});
    }
  };

  // Auswahl auf noch existierende Items beschränken (nach Entfernen etc.).
  const allIdSet = React.useMemo(() => new Set(items.map((i) => i.id)), [items]);
  React.useEffect(() => {
    setSelected((prev) => {
      const next = new Set([...prev].filter((id) => allIdSet.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [allIdSet]);

  const toggleOne = React.useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const setManySelected = React.useCallback((ids: string[], on: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        if (on) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }, []);

  const sorted = React.useMemo(() => {
    const ts = (i: QueueItem) => new Date(i.updatedAt || i.createdAt || 0).getTime() || 0;
    const copy = [...items];
    if (sort === "name") copy.sort((a, b) => itemLabel(a).localeCompare(itemLabel(b), "de"));
    else if (sort === "old") copy.sort((a, b) => ts(a) - ts(b));
    else copy.sort((a, b) => ts(b) - ts(a));
    return copy;
  }, [items, sort]);

  // „queued" (wartet auf freien Render-Slot) wird in der Rendering-Spalte gezeigt.
  const effStatus = (i: QueueItem): QueueItem["status"] => {
    const s = i.runtime?.status ?? i.status;
    return s === "queued" ? "rendering" : s;
  };
  const buckets = COLS.map((col) => ({
    ...col,
    items: sorted.filter((i) => effStatus(i) === col.key),
  }));
  const activeCount = items.filter((i) => (i.runtime?.status ?? i.status) === "rendering").length;
  const waitingCount = items.filter((i) => i.runtime?.status === "queued").length;

  const readyFileCount = React.useMemo(
    () => items.reduce((n, i) => n + (i.outputs?.length ?? 0), 0),
    [items],
  );

  // ---- Bulk-Aktionen (laufen serverseitig weiter, Browser darf zu) ----
  const bulkRender = async (ids: string[]) => {
    if (!ids.length || busy) return;
    setBusy(true);
    try {
      const r = await api.queueBatch(ids, "render");
      setManySelected(ids, false);
      await refresh();
      void r;
    } finally {
      setBusy(false);
    }
  };

  const bulkRemove = async (ids: string[], colLabel: string) => {
    if (!ids.length || busy) return;
    if (!confirm(`${ids.length} Item(s) aus „${colLabel}" entfernen?`)) return;
    setBusy(true);
    try {
      await api.queueBatch(ids, "remove");
      setManySelected(ids, false);
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const totalSelected = selected.size;

  return (
    <section>
      <div className="section-title">
        <h2>Queue</h2>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {totalSelected > 0 && (
            <button className="btn ghost sm" onClick={() => setSelected(new Set())} title="Auswahl aufheben">
              ✕ {totalSelected} gewählt
            </button>
          )}
          {readyFileCount > 0 && (
            <a
              className="btn primary"
              href={api.downloadAllOutputsUrl()}
              style={{ display: "inline-flex", alignItems: "center", textDecoration: "none" }}
              title="Alle fertigen Renders als ZIP herunterladen (High Quality)"
            >
              ⬇ Alle herunterladen ({readyFileCount}) · ZIP
            </a>
          )}
          <label className="queue-sort" title="Wie viele Renders maximal gleichzeitig laufen dürfen">
            <span>Parallel</span>
            <select value={concurrency} onChange={(e) => void onConcurrencyChange(Number(e.target.value))}>
              {Array.from({ length: maxConcurrency }, (_, i) => i + 1).map((n) => (
                <option key={n} value={n}>
                  {n}× gleichzeitig
                </option>
              ))}
            </select>
          </label>
          <label className="queue-sort">
            <span>Sortieren</span>
            <select value={sort} onChange={(e) => setSort(e.target.value as SortKey)}>
              {SORTS.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
          <span className="hint">
            {items.length} insgesamt
            {activeCount > 0 || waitingCount > 0
              ? ` · ${activeCount} aktiv${waitingCount > 0 ? `, ${waitingCount} wartend` : ""}`
              : ""}
          </span>
        </div>
      </div>
      <div className="kanban">
        {buckets.map((col) => {
          const colIds = col.items.map((i) => i.id);
          const selInCol = colIds.filter((id) => selected.has(id));
          const allColSelected = colIds.length > 0 && selInCol.length === colIds.length;
          const someColSelected = selInCol.length > 0 && !allColSelected;
          const doneIdsSel = col.key === "done" ? selInCol : [];
          return (
            <div key={col.key} className="kanban-col">
              <h3>
                {colIds.length > 0 && (
                  <input
                    type="checkbox"
                    className="kanban-col-check"
                    checked={allColSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someColSelected;
                    }}
                    onChange={() => setManySelected(colIds, !allColSelected)}
                    title="Alle in dieser Spalte auswählen"
                  />
                )}
                {col.label}
                <span className="count">{col.items.length}</span>
              </h3>

              {selInCol.length > 0 && (
                <div className="kanban-bulk">
                  <span className="kanban-bulk-count">{selInCol.length} gewählt</span>
                  {col.key === "pending" && (
                    <button className="btn primary sm" disabled={busy} onClick={() => void bulkRender(selInCol)}>
                      {busy ? "…" : `▶ Rendern`}
                    </button>
                  )}
                  {col.key === "failed" && (
                    <button className="btn primary sm" disabled={busy} onClick={() => void bulkRender(selInCol)}>
                      {busy ? "…" : `↻ Neu rendern`}
                    </button>
                  )}
                  {col.key === "done" && doneIdsSel.length > 0 && (
                    <a
                      className="btn primary sm"
                      href={api.downloadAllOutputsUrl(doneIdsSel)}
                      style={{ textDecoration: "none" }}
                      title="Ausgewählte als ZIP herunterladen"
                    >
                      ⬇ ZIP
                    </a>
                  )}
                  <button
                    className="btn danger sm"
                    disabled={busy}
                    onClick={() => void bulkRemove(selInCol, col.label)}
                    title="Ausgewählte entfernen"
                  >
                    🗑
                  </button>
                </div>
              )}

              {col.items.length === 0 ? (
                <div className="empty" style={{ padding: 14, fontSize: 12 }}>
                  {col.key === "pending"
                    ? "Nichts in der Warteschlange. Geh in den Editor."
                    : col.key === "done"
                      ? "Noch keine fertigen Renders."
                      : col.key === "failed"
                        ? "Keine fehlgeschlagenen Renders."
                        : "Aktuell rendert nichts."}
                </div>
              ) : (
                col.items.map((it) => (
                  <Card
                    key={it.id}
                    item={it}
                    refresh={refresh}
                    selected={selected.has(it.id)}
                    onToggle={toggleOne}
                  />
                ))
              )}
            </div>
          );
        })}
      </div>
      {items.length > 0 && (
        <div style={{ marginTop: 22, display: "flex", justifyContent: "flex-end" }}>
          <button
            className="btn ghost"
            onClick={async () => {
              if (!confirm("Komplette Queue leeren?")) return;
              await api.clearQueue();
              await refresh();
            }}
          >
            Queue leeren
          </button>
        </div>
      )}
    </section>
  );
};
